import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
// Language routes import removed - will be implemented later
import { createCrmRoutes, crmGuard } from './crmRoutes'
import { createTFRoutes, tfGuard } from './tfRoutes'
import { useAuthStore } from '@/stores/authStore'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Home route (CRITICAL - prevents infinite redirect loop)
    {
      path: '/',
      name: 'home',
      component: () => import('../views/core/Home.vue'),
      meta: { 
        title: 'Home',
        canonical: '/', // ✅ Opt-in: Homepage gets canonical
      },
    },

    // Legal pages - public routes (add until languageRoutes are re-enabled)
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/legal/PrivacyPolicy.vue'),
      meta: {
        title: 'Privacy Policy',
        canonical: '/privacy',
      },
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/legal/TermsOfService.vue'),
      meta: {
        title: 'Terms of Service',
        canonical: '/terms',
      },
    },
    {
      path: '/terms-of-service',
      redirect: '/terms',
    },
    {
      path: '/affiliate-disclosure',
      name: 'affiliate-disclosure',
      component: () => import('../views/legal/AffiliateDisclosure.vue'),
      meta: {
        title: 'Affiliate Disclosure',
        canonical: '/affiliate-disclosure',
      },
    },
    {
      path: '/cookies',
      name: 'cookies',
      component: () => import('../views/legal/Cookies.vue'),
      meta: {
        title: 'Cookie Policy',
        canonical: '/cookies',
      },
    },
    {
      path: '/accessibility',
      name: 'accessibility',
      component: () => import('../views/legal/Accessibility.vue'),
      meta: {
        title: 'Accessibility Statement',
        canonical: '/accessibility',
      },
    },

    // CRM Routes (Protected) - NO canonical (internal/admin pages)
    ...createCrmRoutes(),

    // TF Routes (TakeFav Organizational System) - NO canonical (user-specific content)
    ...createTFRoutes(),

    // Auth routes - NO canonical (login/signup pages)
    {
      path: '/sign-in',
      name: 'sign-in',
      component: () => import('../views/auth/SignIn.vue'),
      meta: { 
        title: 'Sign In',
        // ❌ No canonical - auth pages shouldn't be indexed
      },
    },
    {
      path: '/sign-up',
      name: 'sign-up',
      component: () => import('../views/auth/SignUp.vue'),
      meta: { 
        title: 'Sign Up',
        // ❌ No canonical - auth pages shouldn't be indexed
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/auth/ForgotPassword.vue'),
      meta: { 
        title: 'Forgot Password',
        // ❌ No canonical - auth pages shouldn't be indexed
      },
    },
    {
      path: '/confirm-email',
      name: 'confirm-email',
      component: () => import('../views/auth/ConfirmEmail.vue'),
      meta: { 
        title: 'Confirm Email',
        // ❌ No canonical - auth pages shouldn't be indexed
      },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/auth/ResetPassword.vue'),
      meta: { 
        title: 'Reset Password',
        // ❌ No canonical - auth pages shouldn't be indexed
      },
    },

    // Favorites route - NO canonical (user-specific content)
    {
      path: '/favorites',
      name: 'favorites',
      component: () => import('../views/core/FavoritesView.vue'),
      meta: { 
        title: 'My Favorites',
        // ❌ No canonical - user-specific content
      },
    },

    // Profile route - NO canonical (user-specific content)
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/auth/Profile.vue'),
      meta: {
        title: 'My Profile',
        requiresAuth: true,
        // ❌ No canonical - user-specific content
      },
    },

    // Public routes - ✅ Opt-in: Public content gets canonical
    {
      path: '/public',
      name: 'public-feed',
      component: () => import('../views/public/PublicFeed.vue'),
      meta: {
        title: 'Public Discoveries',
        canonical: '/public', // ✅ Public discovery feed should be indexed
      },
    },
    {
      path: '/public/user/:id',
      name: 'public-user-profile-legacy',
      component: () => import('../views/public/PublicUserProfile.vue'),
      meta: {
        title: 'User Profile',
        // ❌ No canonical - legacy route, redirects to username-based route
      },
    },
    {
      path: '/public/:username',
      name: 'public-user-profile',
      component: () => import('../views/public/PublicUserProfile.vue'),
      meta: {
        title: 'User Profile',
        canonical: (route: RouteLocationNormalized) => `/public/${String(route.params.username).toLowerCase()}`, // ✅ Public user profiles should be indexed (lowercase username)
      },
    },

    // Articles list route - ✅ Opt-in: Public content gets canonical
    {
      path: '/articles',
      name: 'articles',
      component: () => import('../views/articles/ArticlesList.vue'),
      meta: { 
        title: 'Articles',
        canonical: '/articles',
      },
    },

    // Products list route - ✅ Opt-in: Public content gets canonical
    {
      path: '/products',
      name: 'products',
      component: () => import('../views/products/ProductsList.vue'),
      meta: { 
        title: 'Products',
        canonical: '/products',
      },
    },

    // Categories route - ✅ Opt-in: Public content gets canonical
    {
      path: '/category/:slug',
      name: 'category',
      component: () => import('../views/categories/CategoryView.vue'),
      meta: { 
        title: 'Category',
        canonical: (route: RouteLocationNormalized) => `/category/${String(route.params.slug).toLowerCase()}`
      },
    },

    // Products route - ✅ Opt-in: Public content gets canonical
    {
      path: '/product/:slug',
      name: 'product',
      component: () => import('../views/products/ProductView.vue'),
      meta: { 
        title: 'Product',
        canonical: (route: RouteLocationNormalized) => `/product/${String(route.params.slug).toLowerCase()}`
      },
    },

    // Articles route - ✅ Opt-in: Public content gets canonical
    {
      path: '/article/:slug',
      name: 'article',
      component: () => import('../views/articles/Article.vue'),
      meta: { 
        title: 'Article',
        canonical: (route: RouteLocationNormalized) => `/article/${String(route.params.slug).toLowerCase()}`
      },
    },

    // Human-friendly HTML sitemap - ✅ Opt-in: Public page gets canonical
    {
      path: '/sitemap',
      name: 'SitemapPage',
      component: () => import('@/views/SitemapPage.vue'),
      meta: { 
        title: 'Sitemap',
        canonical: '/sitemap',
      },
    },

    // NOTE: /sitemap.xml, /sitemap-*.xml and /rss.xml are NOT routes.
    // They are real static files generated at build time by
    // scripts/generate-sitemaps.js and served straight from frontend/public/.
    // They used to be Vue routes, which meant Cloudflare served the SPA shell
    // (Content-Type: text/html) for them. Google's sitemap and feed fetchers do
    // not execute JavaScript, so every URL inside them was invisible.
    // Do not re-add them here.

    // Catch-all redirect to home (only for non-root paths to prevent infinite loop)
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})

// Navigation guards
router.beforeEach((to, from, next) => {
  // Set page title
  if (to.meta?.title) {
    document.title = `${to.meta.title} - TakeFav`
  } else {
    document.title = 'TakeFav'
  }

  // Check authentication for protected routes
  if (to.meta?.requiresAuth) {
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      next({ name: 'sign-in', query: { redirect: to.fullPath } })
      return
    }
  }

  // CRM route protection
  if (to.path.startsWith('/crm')) {
    crmGuard(to, from, next)
    return
  }

  // TF route protection
  if (to.path.startsWith('/tf')) {
    tfGuard(to, from, next)
    return
  }

  next()
})

// Scroll to top on route change
router.afterEach((to, from) => {
  // Scroll to top when navigating to a new page
  if (to.path !== from.path) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }
})

export default router
